import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckIcon, ChevronDownIcon, GiftIcon, HelpCircleIcon, QuoteIcon, ShieldIcon, TrophyIcon, XIcon } from 'lucide-react';
import { bonuses, developedTestimonials, faqs, modules, pains, purchaseUrl, testimonials } from '../data/content';
import { Button } from './ui/Button';
const collapseTransition = {
  duration: 0.22,
  ease: [0.23, 1, 0.32, 1] as const
};
function Eyebrow({
  children,
  danger = false
}: {
  children: React.ReactNode;
  danger?: boolean;
}) {
  return <p className={`mb-3 text-center text-[11px] font-extrabold uppercase tracking-[.16em] md:text-sm ${danger ? 'text-red-500' : 'text-primary'}`} data-id="element-18" data-mp="1no478ia3clrt">
      {children}
    </p>;
}
function SectionTitle({
  children
}: {
  children: React.ReactNode;
}) {
  return <h2 className="mx-auto max-w-3xl text-center text-2xl font-black leading-tight text-ink md:text-4xl" data-id="element-19" data-mp="2z2zwey6d65mm">
      {children}
    </h2>;
}
export function Testimonials() {
  return <section className="mx-auto max-w-6xl px-4 py-12 md:py-14" data-id="element-20" data-mp="1bodrvpdke6x9">
      <Eyebrow data-id="element-21" data-mp="16q2gha7mfocx">⭐ 4,9/5 · +200 personas entrenando</Eyebrow>
      <SectionTitle data-id="element-22" data-mp="16ohjzf1qxvcf">
        Lo que dicen los que <span className="text-primary" data-id="element-23" data-mp="12oh4dr2wve3s">ya lo están usando</span>
      </SectionTitle>
      <div className="mt-8 grid gap-4 md:grid-cols-3" data-id="element-24" data-mp="xq5whlw6fdo7">
        {testimonials.map(item => <article key={item.name} className="relative flex flex-col rounded-2xl border border-line bg-canvas p-6 shadow-sm" data-id="element-25" data-mp="sqt1uez2d2b2">
            <QuoteIcon className="absolute right-5 top-5 h-7 w-7 text-primary/15" data-id="element-26" data-mp="nyrqcv9pzfm3" />
            <div className="mb-3 text-sm text-gold" data-id="element-27" data-mp="j1szqmp9lqsn">⭐⭐⭐⭐⭐</div>
            <p className="mb-5 flex-1 text-base font-semibold italic leading-relaxed text-ink/80" data-id="element-28" data-mp="ezxz7kf5rhfx">
              “{item.text}”
            </p>
            <div className="mt-auto flex items-center gap-3 border-t border-line pt-4" data-id="element-29" data-mp="9zs3nt5rpask">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border-2 border-primary/25 bg-white text-sm font-black text-primary" data-id="element-30" data-mp="52jgfe5rigkg">
                {item.initials}
              </span>
              <div data-id="element-31" data-mp="e0ni789hxp9">
                <p className="text-sm font-extrabold text-ink" data-id="element-32" data-mp="act75uzt79h">{item.name}</p>
                <p className="text-xs text-ink/55" data-id="element-33" data-mp="3hmnjoklycakp">📍 {item.place}</p>
              </div>
            </div>
          </article>)}
      </div>
    </section>;
}
export function Pain() {
  return <section className="border-y border-line bg-white px-4 py-12 md:py-14" data-id="element-34" data-mp="e4vutf3446qq">
      <div className="mx-auto max-w-4xl" data-id="element-35" data-mp="j1vv19da6f9x">
        <Eyebrow danger data-id="element-36" data-mp="4eaqrc4o6a0f">😤 La realidad</Eyebrow>
        <SectionTitle data-id="element-37" data-mp="4dj62wcm1wox">
          ¿Cuántos meses más vas a{' '}
          <span className="text-primary" data-id="element-38" data-mp="9d47ddf96h4t">improvisar entrenamientos que no dan resultados?</span>
        </SectionTitle>
        <div className="mt-8 grid gap-3 sm:grid-cols-2" data-id="element-39" data-mp="x2q1vnkok86h">
          {pains.map(pain => <div key={pain.title} className="flex items-start gap-3 rounded-xl border border-line bg-canvas p-4" data-id="element-40" data-mp="11u81o8ayw2pv">
              <XIcon className="mt-0.5 h-5 w-5 shrink-0 text-red-500" data-id="element-41" data-mp="2mi0qbov7awy8" />
              <span data-id="element-42" data-mp="2hk7kiwxt84yh">
                <b className="block text-sm font-black text-ink md:text-base" data-id="element-43" data-mp="2hiwkn87hjhcn">{pain.title}</b>
                <span className="mt-1 block text-sm font-semibold leading-relaxed text-ink/70" data-id="element-44" data-mp="2w93qmogb37wg">
                  {pain.text}
                </span>
              </span>
            </div>)}
        </div>
        <p className="mt-8 text-center text-lg font-bold text-ink/80 md:text-2xl" data-id="element-45" data-mp="23nc3oibbh97v">
          No es falta de disciplina.{' '}
          <span className="font-black text-primary" data-id="element-46" data-mp="2clo236ifdbav">Es falta de un sistema.</span>
        </p>
        <div className="mt-7 text-center" data-id="element-47" data-mp="28kcaivfzqg01">
          <Button href="#pricing" className="w-full text-sm md:w-auto md:text-base" data-id="element-48" data-mp="28jjaedbjeoqv">
            SÍ, QUIERO UN PLAN QUE FUNCIONE →
          </Button>
        </div>
      </div>
    </section>;
}
export function ProductOverview() {
  const [open, setOpen] = useState(0);
  return <section className="mx-auto max-w-4xl px-4 py-12 md:py-14" data-id="element-49" data-mp="j51t0m0lq7o4">
      <Eyebrow data-id="element-50" data-mp="n5vfdayebeew">
        <span className="inline-flex items-center gap-2" data-id="element-51" data-mp="n5lrpu0tpjcd">
          <TrophyIcon className="h-4 w-4" data-id="element-52" data-mp="n654kwbm9hjj" /> Qué incluye el sistema
        </span>
      </Eyebrow>
      <SectionTitle data-id="element-53" data-mp="s3n7a4z1j0j3">
        5 módulos progresivos. <span className="text-primary" data-id="element-54" data-mp="x4knwd4ejmih">6 meses de entrenamiento.</span>
      </SectionTitle>
      <p className="mt-3 text-center font-semibold text-ink/70" data-id="element-55" data-mp="123e0m3bv79hl">
        350+ páginas con ejercicios, rutinas, progresión y bonos listos para usar.
      </p>

      <div className="mt-8 space-y-3" data-id="element-56" data-mp="162wo96v2lyo4">
        {modules.map((mod, index) => <div key={mod.name} className={`overflow-hidden rounded-2xl border bg-white transition-colors duration-150 ease-out ${open === index ? 'border-primary shadow-lg shadow-mist' : 'border-line'}`} data-id="element-57" data-mp="1awr2hurwplft" data-mpi={index}>
            <button className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left md:px-6" aria-expanded={open === index} onClick={() => setOpen(open === index ? -1 : index)} data-id="element-58" data-mp="1krssesu0arjg">
              <span className="flex items-center gap-3" data-id="element-59" data-mp="35amr5vhoitw0">
                <span className="text-2xl" data-id="element-60" data-mp="30clcdmradgqc">{mod.icon}</span>
                <span data-id="element-61" data-mp="30d3e2ywpwn5n">
                  <b className="block text-sm font-black uppercase text-ink md:text-base" data-id="element-62" data-mp="30eek75dmy6zx">
                    {mod.name}
                  </b>
                  <small className="font-bold text-primary" data-id="element-63" data-mp="3t1ihspaefj1o">{mod.period}</small>
                </span>
              </span>
              <ChevronDownIcon className={`h-5 w-5 shrink-0 text-primary transition-transform duration-200 ease-out ${open === index ? 'rotate-180' : ''}`} data-id="element-64" data-mp="3o3h1gc4cxxtw" />
            </button>
            <AnimatePresence initial={false} data-id="element-65" data-mp="3ebtaecbpqisy">
              {open === index && <motion.div initial={{
            height: 0,
            opacity: 0
          }} animate={{
            height: 'auto',
            opacity: 1
          }} exit={{
            height: 0,
            opacity: 0
          }} transition={collapseTransition} data-id="element-66" data-mp="fn4cd769nabh">
                  <ul className="space-y-2 border-t border-line px-5 py-4 text-sm font-semibold text-ink/70 md:px-6" data-id="element-67" data-mp="2a2b3n4dqzkh7">
                    {mod.content.map(line => <li key={line} className="flex gap-2" data-id="element-68" data-mp="1vbkc7xg2inem">
                        <span className="text-primary" data-id="element-69" data-mp="20bpzl1fb1nqw">→</span>
                        {line}
                      </li>)}
                  </ul>
                </motion.div>}
            </AnimatePresence>
          </div>)}
      </div>

      <div className="mt-9 rounded-2xl border-2 border-dashed border-primary/35 bg-canvas p-6 text-center" data-id="element-70" data-mp="1cfc3ukrrdwf3">
        <p className="text-base font-black text-ink md:text-xl" data-id="element-71" data-mp="1hd3w5fwbfkjt">
          Todo esto ya está armado y esperándote adentro.
        </p>
        <p className="mt-1 text-sm font-semibold text-ink/70" data-id="element-72" data-mp="36td5fdep0xiv">
          No hay nada que planificar: abrís el PDF de la semana que toca y entrenás.
        </p>
        <div className="mt-5" data-id="element-73" data-mp="103ruxpsfcdy0">
          <Button href={purchaseUrl} variant="forest" className="w-full text-sm md:w-auto md:text-base" data-id="element-74" data-mp="1052x5l681dn0">
            💥 DESBLOQUEAR MIS 6 MESES POR $19.990 →
          </Button>
        </div>
      </div>
    </section>;
}
export function BonusesSection() {
  return <section className="border-y border-line bg-white px-4 py-12 md:py-14" data-id="element-75" data-mp="152ct7jo48njt">
      <div className="mx-auto max-w-6xl" data-id="element-76" data-mp="1itrddttreusg">
        <Eyebrow data-id="element-77" data-mp="1dvzoz9sbpqt0">
          <span className="inline-flex items-center gap-2" data-id="element-78" data-mp="1dwhhpwfs1mw6">
            <GiftIcon className="h-4 w-4" data-id="element-79" data-mp="1dy2dfpdtvbvm" /> 3 bonos siempre incluidos
          </span>
        </Eyebrow>
        <SectionTitle data-id="element-80" data-mp="1sr5tttfonjkr">
          Además te llevás <span className="text-primary" data-id="element-81" data-mp="1ntm3hv5pngcg">3 bonos gratis</span>
        </SectionTitle>
        <div className="mt-8 grid gap-4 md:grid-cols-3" data-id="element-82" data-mp="3ukbv94yjuvb3">
          {bonuses.map(bonus => <article key={bonus.number} className="flex flex-col rounded-2xl border border-line bg-canvas p-5 shadow-sm" data-id="element-83" data-mp="3pls85187zdh5">
              <div className="flex gap-3" data-id="element-84" data-mp="33s32p14q4gzg">
                <span className="text-3xl" data-id="element-85" data-mp="38qwgkunxaa05">{bonus.icon}</span>
                <div data-id="element-86" data-mp="38pczx2yctwxu">
                  <small className="font-extrabold uppercase tracking-wide text-primary" data-id="element-87" data-mp="3di62bk6cot6r">
                    Bono {bonus.number}
                  </small>
                  <h3 className="text-base font-black uppercase leading-tight text-ink" data-id="element-88" data-mp="3igptbyzswv5z">
                    {bonus.name}
                  </h3>
                </div>
              </div>
              <p className="mt-4 text-sm font-semibold leading-relaxed text-ink/70" data-id="element-89" data-mp="2ptd4fi664b9i">
                {bonus.description}
              </p>
              <ul className="my-4 flex flex-1 flex-wrap gap-1.5" data-id="element-90" data-mp="2uu15yxs0wahz">
                {bonus.tags.map(tag => <li key={tag} className="rounded-full border border-line bg-white px-2.5 py-1 text-[11px] font-bold text-ink/70" data-id="element-91" data-mp="a1fv51z18a3h">
                    {tag}
                  </li>)}
              </ul>
              <div className="mt-auto flex items-center border-t border-line pt-4 text-sm font-extrabold" data-id="element-92" data-mp="1yn671fth7gcl">
                <span className="text-ink/40 line-through" data-id="element-93" data-mp="1ulkw400i5c51">{bonus.value}</span>
                <span className="ml-auto rounded-full bg-primary/10 px-3 py-1 text-primary" data-id="element-94" data-mp="1pr6vnj6zgeti">
                  GRATIS
                </span>
              </div>
            </article>)}
        </div>
        <p className="mt-7 text-center text-base font-bold text-ink/70 md:text-lg" data-id="element-95" data-mp="1fvvjn8myllpg">
          Valor total de bonos: <s className="text-ink/40" data-id="element-96" data-mp="1axlogy18il3u">$34.000 ARS</s> →{' '}
          <span className="font-black text-primary" data-id="element-97" data-mp="16uxayd1ksq0j">incluidos gratis hoy</span>
        </p>
      </div>
    </section>;
}
export function ResultsAndGuarantee() {
  return <section className="mx-auto max-w-5xl px-4 py-12 md:py-14" data-id="element-98" data-mp="317n6ez7le7a1">
      <Eyebrow data-id="element-99" data-mp="315u9ibms71f8">❤️ Resultados reales</Eyebrow>
      <SectionTitle data-id="element-100" data-mp="2w8aieb4ziu5v">
        Personas que cambiaron su cuerpo y su{' '}
        <span className="text-primary" data-id="element-101" data-mp="2554q1nl8q22a">consistencia con este programa</span>
      </SectionTitle>

      <div className="mt-8 space-y-4" data-id="element-102" data-mp="2061vwy6stg93">
        {developedTestimonials.map(item => <article key={item.name} className="rounded-2xl border border-line bg-canvas p-5 shadow-sm md:flex md:gap-6 md:p-6" data-id="element-103" data-mp="208ead1dcl8he">
            <div className="flex items-center gap-3 md:w-60 md:shrink-0 md:flex-col md:items-start" data-id="element-104" data-mp="2981brn62ueag">
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border-2 border-primary/25 bg-white text-sm font-black text-primary" data-id="element-105" data-mp="1lhyl9bht9gdy">
                {item.initials}
              </span>
              <div data-id="element-106" data-mp="1v89vffs38lta">
                <p className="text-sm font-extrabold text-ink" data-id="element-107" data-mp="1qg8kbx6np14u">{item.name}</p>
                <p className="text-xs font-semibold leading-snug text-ink/55" data-id="element-108" data-mp="1qexdfohwzd9i">📍 {item.place}</p>
              </div>
            </div>
            <div className="mt-4 border-t border-line pt-4 md:mt-0 md:flex-1 md:border-l md:border-t-0 md:pl-6 md:pt-0" data-id="element-109" data-mp="xrujzas3hsq4">
              <div className="flex flex-wrap items-center gap-3" data-id="element-110" data-mp="39eo9wr84o28j">
                <span className="text-sm text-gold" data-id="element-111" data-mp="3e5mw27fsrujp">⭐⭐⭐⭐⭐</span>
                <span className="rounded-full bg-primary/10 px-3 py-1 text-[11px] font-extrabold uppercase tracking-wide text-primary" data-id="element-112" data-mp="3e77s61hrfljo">
                  {item.result}
                </span>
              </div>
              <p className="mt-3 text-sm font-semibold italic leading-relaxed text-ink/80" data-id="element-113" data-mp="35b8hk733co1n">
                “{item.text}”
              </p>
            </div>
          </article>)}
      </div>
    </section>;
}
export function Guarantee() {
  return <section className="bg-white px-4 pb-12 md:pb-14" data-id="element-114" data-mp="3j5j20c821w1w">
      <div className="mx-auto flex max-w-5xl flex-col items-center gap-5 rounded-3xl border-2 border-primary/25 bg-canvas p-6 text-center shadow-sm md:flex-row md:p-8 md:text-left" data-id="element-115" data-mp="3j73rvojfcqtf">
        <span className="inline-flex shrink-0 rounded-full border-2 border-primary bg-white p-4" data-id="element-116" data-mp="27rnkiwtohb7i">
          <ShieldIcon className="h-9 w-9 text-primary" data-id="element-117" data-mp="2cmkqtvzkp6gp" />
        </span>
        <div data-id="element-118" data-mp="1sl9fksl4a52u">
          <h2 className="text-lg font-black uppercase text-ink md:text-xl" data-id="element-119" data-mp="1sm18fvnbxopf">
            Garantía de 7 días, sin preguntas
          </h2>
          <p className="mt-2 text-sm font-semibold leading-relaxed text-ink/70" data-id="element-120" data-mp="1ju94m3st9dtt">
            Abrís el programa y lo usás en tu entrenamiento. Si no es lo que esperabas, escribís a
            compradigitalyaa@gmail.com y te devolvemos el 100% de tu dinero. Sin formularios, sin
            drama, sin explicaciones. Punto. Tasa de devoluciones:{' '}
            <b className="text-primary" data-id="element-121" data-mp="14w6wrheqy66r">menos del 0,3%</b> porque realmente funciona.
          </p>
        </div>
      </div>
    </section>;
}
export function FAQSection() {
  const [open, setOpen] = useState(0);
  return <section className="border-t border-line bg-white px-4 py-12 md:py-14" data-id="element-122" data-mp="vyd6an3ny6ri">
      <div className="mx-auto max-w-3xl" data-id="element-123" data-mp="2v8w4fsbrxzwj">
        <Eyebrow data-id="element-124" data-mp="2qabdykmbqula">
          <span className="inline-flex items-center gap-2" data-id="element-125" data-mp="2q9rxz9g8ig9w">
            <HelpCircleIcon className="h-4 w-4" data-id="element-126" data-mp="2q88d15jmvh1s" /> Preguntas frecuentes
          </span>
        </Eyebrow>
        <SectionTitle data-id="element-127" data-mp="2xrztyrk95ge2">
          Lo que nos <span className="text-primary" data-id="element-128" data-mp="32sdp590r8vuo">preguntan siempre</span>
        </SectionTitle>
        <div className="mt-8 space-y-3" data-id="element-129" data-mp="37jvue99t9ilg">
          {faqs.map(([question, answer], index) => <div key={question} className={`overflow-hidden rounded-2xl border bg-white transition-colors duration-150 ease-out ${open === index ? 'border-primary/60 shadow-lg shadow-mist' : 'border-line'}`} data-id="element-130" data-mp="3cigvsc0su8bh" data-mpi={index}>
              <button className="flex w-full items-center justify-between gap-3 p-5 text-left text-sm font-extrabold text-ink md:text-base" onClick={() => setOpen(open === index ? -1 : index)} aria-expanded={open === index} data-id="element-131" data-mp="2jukyp67biee6">
                {question}
                <ChevronDownIcon className={`h-5 w-5 shrink-0 text-primary transition-transform duration-200 ease-out ${open === index ? 'rotate-180' : ''}`} data-id="element-132" data-mp="44ztq0d0op2q" />
              </button>
              <AnimatePresence initial={false} data-id="element-133" data-mp="6p65uvbsblud">
                {open === index && <motion.div initial={{
              height: 0,
              opacity: 0
            }} animate={{
              height: 'auto',
              opacity: 1
            }} exit={{
              height: 0,
              opacity: 0
            }} transition={collapseTransition} className="border-t border-line px-5 py-4 text-sm font-semibold leading-relaxed text-ink/70" data-id="element-134" data-mp="1qcqexcxtkrk">
                    {answer}
                  </motion.div>}
              </AnimatePresence>
            </div>)}
        </div>
      </div>
    </section>;
}
export function Closing() {
  return <section className="bg-canvas px-4 py-12 md:py-16" data-id="element-135" data-mp="ufa0weawqd9r">
      <div className="mx-auto max-w-3xl text-center" data-id="element-136" data-mp="18di2x0msw4rs">
        <h2 className="text-2xl font-black text-ink md:text-4xl" data-id="element-137" data-mp="14b3hq9vnssxc">
          El próximo mes puede ser diferente.
        </h2>
        <p className="mt-3 text-base font-bold text-ink/70 md:text-lg" data-id="element-138" data-mp="1mz5qyww4jtfx">
          O seguís igual los siguientes 6 meses. Tu elección.
        </p>
        <p className="mt-2 text-sm font-semibold italic text-primary" data-id="element-139" data-mp="1rwe94xh9vxh6">
          “¡Finalmente un mes sin abandonar tu entrenamiento!”
        </p>
        <div className="mt-6" data-id="element-140" data-mp="1ixamjjiwczxx">
          <Button href={purchaseUrl} className="w-full text-sm md:w-auto md:px-10 md:text-lg" data-id="element-141" data-mp="1iw90yl498v1y">
            💪 QUIERO CAMBIAR AHORA
          </Button>
        </div>
        <p className="mt-4 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-[11px] font-semibold text-ink/60" data-id="element-142" data-mp="19061xo4p9cj8">
          <span className="flex items-center gap-1" data-id="element-143" data-mp="v9j0ho7k18jg">
            <CheckIcon className="h-3.5 w-3.5 text-primary" data-id="element-144" data-mp="z9t9l9qn8f2j" /> Garantía 7 días
          </span>
          <span className="flex items-center gap-1" data-id="element-145" data-mp="la1x5dfrz6qn">
            <CheckIcon className="h-3.5 w-3.5 text-primary" data-id="element-146" data-mp="lbd41m4ioulz" /> Acceso en 5 minutos
          </span>
          <span className="flex items-center gap-1" data-id="element-147" data-mp="q7tr0czzd2xh">
            <CheckIcon className="h-3.5 w-3.5 text-primary" data-id="element-148" data-mp="2ry2ad3ocht9y" /> Pago seguro
          </span>
        </p>
      </div>
    </section>;
}